import React, { useState, useEffect } from 'react';
import { LandingHeader } from '../../../shared/components/layout/LandingHeader';
import { PageHeader } from '../../../shared/components/layout/pageHeader';
import { CaretDown } from '@phosphor-icons/react';
import { Footer } from '../../../shared/components/layout/Footer';

const faqs = [
  {
    id: 'what-is-theos',
    question: "What is Theos?",
    answer: "Theos is an AI-powered Bible study companion that helps you explore and understand scripture through interactive conversations and personalized insights."
  },
  {
    id: 'how-it-works',
    question: "How does the AI chat work?",
    answer: "Our AI chat allows you to ask questions about the Bible and receive thoughtful, scripture-based responses. It's designed to help you explore biblical concepts, find relevant verses, and deepen your understanding."
  },
  {
    id: 'pricing',
    question: "Is Theos free to use?",
    answer: "Theos offers both free and premium features. While basic access is free, our premium subscription unlocks advanced features like unlimited AI conversations and cross-reference tools."
  },
  {
    id: 'accuracy',
    question: "How accurate is the AI?",
    answer: "Our AI is trained on trusted biblical resources and provides scripture-based responses. However, we encourage users to verify insights with scripture and use Theos as a study companion rather than a sole authority."
  }
];

export default function Support() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    // Get the hash without the # symbol
    const hash = window.location.hash.slice(1);
    if (hash) {
      // Find the FAQ with matching ID
      const faqIndex = faqs.findIndex(faq => faq.id === hash);
      if (faqIndex !== -1) {
        setOpenFaqIndex(faqIndex);
        setHighlightedId(hash);
        // Scroll into view with a slight delay to ensure rendering
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
    // Update URL hash when toggling
    const faq = faqs[index];
    if (openFaqIndex === index) {
      // Remove hash if closing
      history.pushState(null, '', window.location.pathname);
      setHighlightedId(null);
    } else {
      // Add hash if opening
      history.pushState(null, '', `#${faq.id}`);
      setHighlightedId(faq.id);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-24">
      <LandingHeader color="#361E06" />
      
      <div className="max-w-2xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <PageHeader
          headline="Support Center"
          subheadline="Find answers to common questions or reach out to our team"
        />

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-[#361E06] mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={faq.id}
                id={faq.id}
                className={`border rounded-lg overflow-hidden transition-colors duration-200 ${
                  highlightedId === faq.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors duration-150"
                >
                  <h3 className="text-lg font-medium text-[#361E06]">{faq.question}</h3>
                  <CaretDown 
                    weight="bold"
                    className={`text-[#361E06] transition-transform duration-200 ${
                      openFaqIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 text-gray-600 bg-white">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="mt-16 border border-gray-200 rounded-lg p-8">
          <h2 className="text-2xl font-semibold text-[#361E06] mb-4">Need More Help?</h2>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you with any questions or concerns you might have.
          </p>
          <button
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#361E06] hover:bg-[#4A2B0B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#361E06]"
          >
            Contact Support
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
} 